import { HttpParameterCodec, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Environment, EnvironmentService } from '@galaxy/core';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { User } from '../core/user';
import { VApiService } from './vapi.service';

/*
  https://github.com/angular/angular/issues/11058
  New HttpParams class has a different serialization behaviour than decprecated URLSearchParams, which Angular team
  is already realized, but they still have this issue opened. So in here, a temporary solution is a customized encoder to make sure
  marketing-automation can still get exactly same html encoded string as before.
*/
class CustomQueryEncoderHelper implements HttpParameterCodec {
  encodeKey(k: string): string {
    return encodeURIComponent(k);
  }

  encodeValue(v: string): string {
    return encodeURIComponent(v);
  }

  decodeKey(k: string): string {
    return decodeURIComponent(k);
  }

  decodeValue(v: string): string {
    return decodeURIComponent(v);
  }
}

const VBC_HOSTS = new Map<Environment, string>();
VBC_HOSTS.set(Environment.LOCAL, 'vbc-demo.appspot.com');
VBC_HOSTS.set(Environment.DEMO, 'vbc-demo.appspot.com');
VBC_HOSTS.set(Environment.PROD, 'vbc-prod.appspot.com');

export interface VbcUser {
  unifiedUserId?: string;
  firstName?: string;
  createdDateTime: string;
  lastLoginDateTime?: string;
  phoneNumber?: string;
  lastName?: string;
  email: string;
  phone?: string;
}

@Injectable({ providedIn: 'root' })
export class VbcApiService {
  private scheme = '';
  private vbcHost = '';

  constructor(private vapiService: VApiService, private environmentService: EnvironmentService) {
    this.scheme = window.location.protocol.startsWith('https') ? 'https' : 'http';
    this.vbcHost = VBC_HOSTS.get(environmentService.getEnvironment());
  }

  getUser(partnerId: string, userId: string): Observable<VbcUser> {
    return this.vapiService.post(`${this.scheme}://${this.vbcHost}/api/v2/user/get/`, null, {
      params: {
        apiUser: partnerId,
        userId: userId,
      },
    });
  }

  updateUser(
    partnerId: string,
    userId: string,
    firstName: string,
    lastName: string,
    greetingName: string,
    email: string,
    phone: string,
    preferredLocale: string,
  ): Observable<boolean> {
    const params = new HttpParams({
      encoder: new CustomQueryEncoderHelper(),
    })
      .set('apiUser', partnerId)
      .set('userId', userId)
      .set('firstName', firstName)
      .set('lastName', lastName)
      .set('greetingName', greetingName)
      .set('email', email)
      .set('phone', phone)
      .set('preferredLocale', preferredLocale);

    return this.vapiService
      .post(`${this.scheme}://${this.vbcHost}/api/v2/user/update/`, null, {
        params: params,
      })
      .pipe(
        map(() => true),
        catchError(() => {
          return of(false);
        }),
      );
  }

  createUser(
    partnerId: string,
    accountGroupId: string,
    firstName: string,
    lastName: string,
    greetingName: string,
    email: string,
    phone: string,
    welcomeMessageFlag: string,
    emailLocale: string,
    welcomeMessage: string,
  ): Observable<User> {
    let params = new HttpParams({
      encoder: new CustomQueryEncoderHelper(),
    })
      .set('apiUser', partnerId)
      .set('firstName', firstName)
      .set('lastName', lastName)
      .set('greetingName', greetingName)
      .set('email', email)
      .set('phone', phone)
      .set('sendWelcomeEmailFlag', welcomeMessageFlag)
      .set('preferredLocale', emailLocale)
      .set('welcomeMessage', welcomeMessage);
    const postUrl = accountGroupId
      ? `${this.scheme}://${this.vbcHost}/internalApi/v3/user/createAndAssociate/`
      : `${this.scheme}://${this.vbcHost}/api/v2/user/create/`;
    params = accountGroupId ? params.appendAll({ ['pid']: partnerId, ['accountGroupId']: accountGroupId }) : params;
    return this.vapiService.post(postUrl, null, {
      params: params,
    });
  }
}
